import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useState, useEffect, useRef } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import { Button } from "./components/ui/button";
import { Textarea } from "./components/ui/textarea";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Mic, MicOff, Volume2, Settings, Play, Square, RotateCcw, Save, FolderOpen, Trash2, User } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SetupPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    profile_name: "",
    role: "",
    job_description: "",
    resume: "",
    additional_notes: "",
    ai_provider: "emergent",
    api_key: ""
  });
  const [savedProfiles, setSavedProfiles] = useState([]);
  const [showProfileDialog, setShowProfileDialog] = useState(false);

  useEffect(() => {
    const profiles = JSON.parse(localStorage.getItem('savedProfiles') || '[]');
    setSavedProfiles(profiles);

    const savedData = localStorage.getItem('interviewContext');
    if (savedData) {
      const parsed = JSON.parse(savedData);
      setFormData({
        profile_name: parsed.profile_name || "",
        role: parsed.role || "",
        job_description: parsed.job_description || "",
        resume: parsed.resume || "",
        additional_notes: parsed.additional_notes || "",
        ai_provider: parsed.ai_provider || "emergent",
        api_key: parsed.api_key || ""
      });
    }
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.role || !formData.job_description || !formData.resume) {
      return;
    }

    if (formData.ai_provider !== "emergent" && formData.ai_provider !== "ollama" && formData.ai_provider !== "groq" && !formData.api_key) {
      return;
    }

    localStorage.removeItem('conversationHistory');
    localStorage.setItem('interviewContext', JSON.stringify(formData));
    navigate('/interview');
  };

  const saveProfile = () => {
    if (!formData.profile_name) {
      return;
    }
    if (!formData.role || !formData.job_description || !formData.resume) {
      return;
    }

    const profiles = JSON.parse(localStorage.getItem('savedProfiles') || '[]');
    const existingIndex = profiles.findIndex(p => p.profile_name === formData.profile_name);

    if (existingIndex >= 0) {
      profiles[existingIndex] = formData;
    } else {
      profiles.push(formData);
    }

    localStorage.setItem('savedProfiles', JSON.stringify(profiles));
    setSavedProfiles(profiles);
  };

  const loadProfile = (profile) => {
    setFormData(profile);
    setShowProfileDialog(false);
  };

  const deleteProfile = (profileName) => {
    const profiles = savedProfiles.filter(p => p.profile_name !== profileName);
    localStorage.setItem('savedProfiles', JSON.stringify(profiles));
    setSavedProfiles(profiles);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-0 md:p-6">
      <Card className="w-full max-w-3xl shadow-2xl border-0" data-testid="setup-card">
        <CardHeader className="space-y-2 pb-6">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent" data-testid="setup-title">
                🎤 Jeevan AI - Interview Companion
              </CardTitle>
              <CardDescription className="text-base mt-2" data-testid="setup-description">
                A friendly AI companion helping you ace every single interview
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Dialog open={showProfileDialog} onOpenChange={setShowProfileDialog}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="icon" title="Load Profile">
                    <FolderOpen className="h-5 w-5" />
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Saved Profiles</DialogTitle>
                    <DialogDescription>
                      Load a previously saved profile or delete old ones
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-3 mt-4">
                    {savedProfiles.length === 0 ? (
                      <p className="text-center text-gray-500 py-8">No saved profiles yet</p>
                    ) : (
                      savedProfiles.map((profile) => (
                        <Card key={profile.profile_name} className="p-4 hover:shadow-md transition-shadow">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <User className="h-4 w-4 text-blue-600" />
                                <h3 className="font-semibold">{profile.profile_name}</h3>
                              </div>
                              <p className="text-sm text-gray-600">Role: {profile.role}</p>
                              <p className="text-sm text-gray-500">AI: {profile.ai_provider}</p>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => loadProfile(profile)}
                                className="bg-blue-600 hover:bg-blue-700"
                              >
                                Load
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => deleteProfile(profile.profile_name)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </Card>
                      ))
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="setup-form">
            <div className="space-y-2 bg-purple-50 p-4 rounded-lg border-2 border-purple-200">
              <Label htmlFor="profile_name" className="text-sm font-bold text-purple-900 flex items-center gap-2">
                <User className="h-4 w-4" />
                Profile Name (for saving)
              </Label>
              <div className="flex gap-2">
                <Input
                  id="profile_name"
                  data-testid="profile-name-input"
                  placeholder="e.g., John - Software Engineer"
                  value={formData.profile_name}
                  onChange={(e) => setFormData({...formData, profile_name: e.target.value})}
                  className="border-purple-300 focus:border-purple-500 focus:ring-purple-500"
                />
                <Button
                  type="button"
                  onClick={saveProfile}
                  className="bg-purple-600 hover:bg-purple-700"
                  title="Save this profile"
                >
                  <Save className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-2 bg-blue-50 p-4 rounded-lg border-2 border-blue-200">
              <Label htmlFor="ai_provider" className="text-sm font-bold text-blue-900">AI Provider *</Label>
              <Select
                value={formData.ai_provider}
                onValueChange={(value) => setFormData({...formData, ai_provider: value})}
              >
                <SelectTrigger className="w-full border-blue-300" data-testid="ai-provider-select">
                  <SelectValue placeholder="Select AI Provider" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="emergent">⚡ Emergent LLM (Fastest & Free) - RECOMMENDED</SelectItem>
                  <SelectItem value="groq">🚀 Groq Llama (Ultra Fast - Free API Key)</SelectItem>
                  <SelectItem value="ollama">🏠 Ollama (Local & Free)</SelectItem>
                  <SelectItem value="openai">OpenAI GPT-4o Mini (Fast)</SelectItem>
                  <SelectItem value="copilot">🤖 Microsoft Copilot (GPT-4o)</SelectItem>
                  <SelectItem value="anthropic">Anthropic Claude Haiku</SelectItem>
                  <SelectItem value="gemini">Google Gemini Flash</SelectItem>
                  <SelectItem value="together">Together AI Llama</SelectItem>
                  <SelectItem value="mistral">Mistral AI</SelectItem>
                  <SelectItem value="deepseek">DeepSeek AI</SelectItem>
                  <SelectItem value="perplexity">Perplexity AI</SelectItem>
                  <SelectItem value="huggingface">🤗 Hugging Face (Phi-3)</SelectItem>
                </SelectContent>
              </Select>

              {formData.ai_provider !== "emergent" && formData.ai_provider !== "ollama" && (
                <div className="mt-3">
                  <Label htmlFor="api_key" className="text-sm font-medium text-blue-900">
                    API Key {formData.ai_provider === "groq" ? "(Optional)" : "*"} {formData.ai_provider === "groq" && <span className="text-xs">(Free at console.groq.com)</span>}
                  </Label>
                  <Input
                    id="api_key"
                    data-testid="api-key-input"
                    type="password"
                    placeholder={`Enter your ${formData.ai_provider.toUpperCase()} API key`}
                    value={formData.api_key}
                    onChange={(e) => setFormData({...formData, api_key: e.target.value})}
                    className="border-blue-300 focus:border-blue-500 focus:ring-blue-500 mt-1"
                  />
                  {formData.ai_provider === "groq" && (
                    <p className="text-xs text-blue-600 mt-1">
                      ✅ Optional: Leave empty to use server's Groq API keys. Or get your own at: <a href="https://console.groq.com/keys" target="_blank" rel="noopener noreferrer" className="underline">console.groq.com/keys</a>
                    </p>
                  )}
                </div>
              )}
              {(formData.ai_provider === "emergent" || formData.ai_provider === "ollama") && (
                <p className="text-xs text-green-700 mt-2">
                  ✅ No API key needed - {formData.ai_provider === "ollama" ? "Local model" : "Free to use"}!
                  {formData.ai_provider === "ollama" && <span className="block mt-1">⚠️ Make sure Ollama is running locally (localhost:11434)</span>}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="role" className="text-sm font-medium">Role/Position *</Label>
              <Input
                id="role"
                data-testid="role-input"
                placeholder="e.g., Senior Software Engineer"
                value={formData.role}
                onChange={(e) => setFormData({...formData, role: e.target.value})}
                required
                className="border-gray-300 focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="job_description" className="text-sm font-medium">Job Description *</Label>
              <Textarea
                id="job_description"
                data-testid="job-description-input"
                placeholder="Paste the job description here..."
                value={formData.job_description}
                onChange={(e) => setFormData({...formData, job_description: e.target.value})}
                required
                rows={6}
                className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 resize-none"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="resume" className="text-sm font-medium">Resume/Background *</Label>
              <Textarea
                id="resume"
                data-testid="resume-input"
                placeholder="Paste your resume or key background information..."
                value={formData.resume}
                onChange={(e) => setFormData({...formData, resume: e.target.value})}
                required
                rows={6}
                className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 resize-none"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="additional_notes" className="text-sm font-medium">Additional Notes (Optional)</Label>
              <Textarea
                id="additional_notes"
                data-testid="additional-notes-input"
                placeholder="Any specific points you want to emphasize..."
                value={formData.additional_notes}
                onChange={(e) => setFormData({...formData, additional_notes: e.target.value})}
                rows={4}
                className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 resize-none"
              />
            </div>

            <Button
              type="submit"
              data-testid="start-interview-btn"
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-medium py-6 text-lg rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Start Interview Session
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

const InterviewPage = () => {
  const navigate = useNavigate();
  const [context, setContext] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [currentAnswer, setCurrentAnswer] = useState("");
  const [lastQuestion, setLastQuestion] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [accumulatedQuestion, setAccumulatedQuestion] = useState("");
  const [conversationHistory, setConversationHistory] = useState([]);
  const [sessionId] = useState(() => `session-${Date.now()}`);
  
  // Whisper recording refs
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const contextRef = useRef(null);
  const conversationHistoryRef = useRef([]);

  useEffect(() => {
    contextRef.current = context;
  }, [context]);

  useEffect(() => {
    conversationHistoryRef.current = conversationHistory;
  }, [conversationHistory]);

  const generateAnswerStreaming = async (question) => {
    const currentContext = contextRef.current;
    if (!currentContext) {
      console.error('No context available');
      return;
    }

    console.log('Generating answer for:', question);
    setIsProcessing(true);
    setCurrentAnswer("");

    try {
      const payload = {
        question: question,
        context: {
          role: currentContext.role,
          job_description: currentContext.job_description,
          resume: currentContext.resume,
          additional_notes: currentContext.additional_notes || ""
        },
        ai_provider: currentContext.ai_provider === "emergent" ? "gemini" : currentContext.ai_provider,
        api_key: (currentContext.ai_provider === "emergent" || currentContext.ai_provider === "ollama" || currentContext.ai_provider === "groq") ? (currentContext.api_key || null) : currentContext.api_key,
        session_id: sessionId,
        conversation_history: conversationHistoryRef.current
      };

      console.log('Sending request to backend...');
      const response = await fetch(`${API}/generate-answer-stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let fullAnswer = '';

      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = JSON.parse(line.slice(6));

            if (data.error) {
              throw new Error(data.error);
            }

            if (data.chunk && !data.done) {
              fullAnswer += data.chunk;
              setCurrentAnswer(fullAnswer);
            }

            if (data.done) {
              if (data.full_answer) {
                fullAnswer = data.full_answer;
                setCurrentAnswer(data.full_answer);
              }

              const newHistory = [
                ...conversationHistoryRef.current,
                { question: question, answer: fullAnswer }
              ];
              setConversationHistory(newHistory);
              localStorage.setItem('conversationHistory', JSON.stringify(newHistory));

              console.log('Answer complete! History updated.');
            }
          }
        }
      }

    } catch (error) {
      console.error('Error:', error);
      setCurrentAnswer(`Error: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSpeechResult = async (transcript) => {
    const trimmed = transcript.trim();
    if (trimmed.length === 0) return;

    console.log('Speech result:', trimmed);
    setLastQuestion(trimmed);
    setAccumulatedQuestion('');
    await generateAnswerStreaming(trimmed);
  };

  const startWhisperRecording = async () => {
    try {
      console.log('🎙️ Starting Whisper recording...');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        console.log('🎙️ Recording stopped, sending to Whisper...');
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        
        // Send to backend for Whisper transcription
        const formData = new FormData();
        formData.append('audio', audioBlob, 'recording.webm');
        
        try {
          setIsProcessing(true);
          const response = await fetch(`${API}/transcribe-whisper`, {
            method: 'POST',
            body: formData
          });
          
          if (!response.ok) {
            throw new Error(`Transcription failed: ${response.status}`);
          }
          
          const result = await response.json();
          const transcript = result.text || result.transcription || '';
          
          if (transcript) {
            console.log('✅ Whisper transcription:', transcript);
            setAccumulatedQuestion(transcript);
            await handleSpeechResult(transcript);
          } else {
            console.error('No transcription received');
            setCurrentAnswer('No speech detected. Please try again.');
            setIsProcessing(false);
          }
        } catch (error) {
          console.error('Transcription error:', error);
          setCurrentAnswer(`Transcription error: ${error.message}`);
          setIsProcessing(false);
        }
        
        // Cleanup mic usage
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsListening(true);
      console.log('🎙️ Whisper recording started');
      
    } catch (error) {
      console.error('Error starting Whisper recording:', error);
      alert('Microphone access denied or not available');
    }
  };

  const stopWhisperRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      console.log('🛑 Stopping Whisper recording...');
      mediaRecorderRef.current.stop();
      setIsListening(false);
    }
  };

  useEffect(() => {
    const savedData = localStorage.getItem('interviewContext');
    if (!savedData) {
      navigate('/');
      return;
    }
    const parsedContext = JSON.parse(savedData);
    setContext(parsedContext);

    const savedHistory = localStorage.getItem('conversationHistory');
    if (savedHistory) {
      setConversationHistory(JSON.parse(savedHistory));
    }

    // Cleanup active recording if component unmounts
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
        mediaRecorderRef.current.stream?.getTracks().forEach(track => track.stop());
      }
    };
  }, [navigate]);

  const toggleListening = () => {
    if (isListening) {
      stopWhisperRecording();
    } else {
      startWhisperRecording();
    }
  };

  const resetConversation = () => {
    setConversationHistory([]);
    setCurrentAnswer("");
    setLastQuestion("");
    localStorage.removeItem('conversationHistory');
  };

  const goToSettings = () => {
    if (isListening) {
      stopWhisperRecording();
    }
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white p-4" data-testid="interview-page">
      <div className="fixed top-4 right-4 z-40 hidden md:flex gap-2">
        <Button
          onClick={resetConversation}
          data-testid="reset-btn"
          variant="outline"
          size="icon"
          className="bg-orange-500/20 backdrop-blur-sm hover:bg-orange-500/40 border-orange-400/30 rounded-full shadow-lg"
          title="Reset conversation"
        >
          <RotateCcw className="h-5 w-5" />
        </Button>
        <Button
          onClick={goToSettings}
          data-testid="settings-btn"
          variant="outline"
          size="icon"
          className="bg-white/10 backdrop-blur-sm hover:bg-white/20 border-white/20 rounded-full shadow-lg"
        >
          <Settings className="h-5 w-5" />
        </Button>
      </div>

      <div className="fixed top-4 left-4 right-4 max-w-4xl mx-auto z-30" data-testid="answer-window">
        <Card className="bg-white/10 backdrop-blur-xl border-white/20 shadow-2xl" style={{backdropFilter: 'blur(24px)'}}>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2 text-white">
              <Volume2 className="h-5 w-5" />
              AI Answer
              {context?.ai_provider && (
                <span className="text-xs bg-blue-500/30 px-2 py-1 rounded">
                  {context.ai_provider === "emergent" ? "⚡ Emergent" :
                   context.ai_provider === "copilot" ? "🤖 Copilot" :
                   context.ai_provider === "groq" ? "🚀 Groq" :
                   context.ai_provider === "ollama" ? "🏠 Ollama" :
                   context.ai_provider === "huggingface" ? "🤗 HuggingFace" :
                   context.ai_provider.toUpperCase()}
                </span>
              )}
              <span className="text-xs bg-purple-500/30 px-2 py-1 rounded">
                🎙️ Whisper
              </span>
              {conversationHistory.length > 0 && (
                <span className="text-xs bg-green-500/30 px-2 py-1 rounded">
                  {conversationHistory.length} Q&A in memory
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isProcessing && !currentAnswer ? (
              <div className="flex items-center gap-2" data-testid="processing-indicator">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span className="text-sm text-gray-200">
                  Transcribing & generating answer...
                </span>
              </div>
            ) : currentAnswer ? (
              <div className="space-y-2">
                <p className="text-sm text-green-300 font-medium" data-testid="question-display">Your question: {lastQuestion}</p>
                <div className="text-base leading-relaxed text-white max-h-[50vh] overflow-y-auto pr-2" data-testid="answer-display">
                  <ReactMarkdown
                    components={{
                      code({ node, inline, className, children, ...props }) {
                        const match = /language-(\w+)/.exec(className || '');
                        return !inline && match ? (
                          <SyntaxHighlighter
                            style={oneDark}
                            language={match[1]}
                            PreTag="div"
                            {...props}
                          >
                            {String(children).replace(/\n$/, '')}
                          </SyntaxHighlighter>
                        ) : (
                          <code className="bg-gray-800 px-1.5 py-0.5 rounded text-sm" {...props}>
                            {children}
                          </code>
                        );
                      }
                    }}
                  >
                    {currentAnswer}
                  </ReactMarkdown>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-gray-400" data-testid="waiting-message">
                  {isListening ? "🎤 Listening... Click STOP when done asking" : "Click START to begin"}
                </p>
                {isListening && (
                  <p className="text-sm text-blue-300 italic">Recording audio...</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-40 flex flex-col items-center gap-3">
        <Button
          onClick={toggleListening}
          data-testid="mic-toggle-btn"
          size="lg"
          disabled={isProcessing}
          className={`rounded-full h-24 w-24 shadow-2xl transition-all duration-300 flex flex-col items-center justify-center ${
            isListening
              ? 'bg-red-500 hover:bg-red-600 animate-pulse'
              : isProcessing
              ? 'bg-gray-500 cursor-not-allowed'
              : 'bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700'
          }`}
        >
          {isListening ? (
            <Mic className="h-10 w-10" />
          ) : (
            <Mic className="h-10 w-10" />
          )}
        </Button>
        <div className="text-center">
          <p className="text-lg font-bold" data-testid="mic-status">
            {isListening ? '🔴 STOP' : isProcessing ? '⏳ PROCESSING' : '▶️ START'}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            {isListening ? 'Click STOP when done' : isProcessing ? 'Generating answer...' : 'Click to START'}
          </p>
        </div>
      </div>

      <div className="fixed bottom-4 left-4 right-4 z-20 max-w-md">
        <Card className="bg-white/5 backdrop-blur-sm border-white/10 shadow-lg">
          <CardContent className="p-4">
            <p className="text-xs text-gray-400" data-testid="role-info">
              Role: <span className="text-white font-medium">{context?.role}</span>
            </p>
            {context?.profile_name && (
              <p className="text-xs text-gray-400 mt-1">
                Profile: <span className="text-white font-medium">{context.profile_name}</span>
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<SetupPage />} />
          <Route path="/interview" element={<InterviewPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
